import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'

import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  darken,
} from '@mui/material'
import {
  IconCircleFilled,
  IconPlus,
  IconSparkles,
  IconBrowser,
  IconCopyPlus,
} from '@tabler/icons-react'
import debounce from 'lodash/debounce'
import { useConfirm } from 'material-ui-confirm'

import MetadataChip from 'src/components/Chips/MetadataChip/MetadataChip'
import Row from 'src/components/Row/Row'
import { logger } from 'src/lib/logger'
import { ObjectTypeMetadata, PropertyTypes } from 'src/lib/objects'
import { modernButtonContainer, shadows } from 'src/lib/style'

import { objectPropertyDefinitionMetadata } from './objectPropertyDefinitions'
import PropertyTest from './PropertyTest'
import {
  anyAiEnabled,
  isPicklist,
  type PropertyDefinitionFormProps,
} from './reducers'

const PropertyDefinitionForm = ({
  workspaceId,
  state,
  dispatch,
  onSubmit,
  onDelete: _onDelete,
  onClose: _onClose,
  onBeforeClose: _onBeforeClose,
  onSaveStatusChange,
}: PropertyDefinitionFormProps & {
  onSaveStatusChange?: (status: 'idle' | 'saving' | 'saved') => void
}) => {
  const confirm = useConfirm()
  const [showNewOptionForm, setShowNewOptionForm] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const initialStateRef = useRef<PropertyDefinitionFormProps['state'] | null>(
    null
  )
  const [newOptionLabel, setNewOptionLabel] = useState('')
  const [newOptionDefinition, setNewOptionDefinition] = useState('')
  const [editingOptionKey, setEditingOptionKey] = useState<string | null>(null)
  const [editedLabel, setEditedLabel] = useState('')
  const [editedDefinition, setEditedDefinition] = useState('')
  const [testMode, setTestMode] = useState(false)

  // Set default property type to TextArea if not set
  useEffect(() => {
    if (!state.propertyType && !state.selectedDefinition) {
      logger.dev('Setting default property type to TextArea')
      dispatch({ type: 'SET_PROPERTY_TYPE', payload: PropertyTypes.TextArea })
    }
  }, [state.propertyType, state.selectedDefinition, dispatch])

  // Auto-save setup (only for edit mode)
  const [isDirty, setIsDirty] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle'
  )
  const isInitialLoad = useRef(true)
  const isEditMode = Boolean(state.selectedDefinition?.id)
  const justCreatedRef = useRef(false)

  // Capture initial state when component mounts or when switching to create mode
  useEffect(() => {
    if (!isEditMode && !initialStateRef.current) {
      initialStateRef.current = { ...state }
    }
  }, [isEditMode, state])

  // Initialize save status to 'saved' when in edit mode
  useEffect(() => {
    if (isEditMode && saveStatus === 'idle') {
      setSaveStatus('saved')
    }
  }, [isEditMode, saveStatus])

  // Notify parent of save status changes
  useEffect(() => {
    onSaveStatusChange?.(saveStatus)
  }, [saveStatus, onSaveStatusChange])

  // Track changes for new properties
  useEffect(() => {
    if (!isEditMode && initialStateRef.current) {
      // Check if any meaningful changes have been made
      const hasChanges =
        state.name !== initialStateRef.current.name ||
        state.description !== initialStateRef.current.description ||
        state.propertyType !== initialStateRef.current.propertyType ||
        state.aiManaged !== initialStateRef.current.aiManaged ||
        state.useWeb !== initialStateRef.current.useWeb ||
        state.options.length !== initialStateRef.current.options.length

      setHasUnsavedChanges(hasChanges)
    }
  }, [state, isEditMode])

  // Set up the beforeClose handler
  useEffect(() => {
    if (_onBeforeClose && !isEditMode) {
      _onBeforeClose.current = async () => {
        if (hasUnsavedChanges) {
          try {
            await confirm({
              title: 'Discard Changes?',
              description:
                'You have unsaved changes. Are you sure you want to discard them?',
              confirmationText: 'Discard',
              cancellationText: 'Continue Editing',
            })
            return true // Allow close
          } catch {
            return false // Prevent close
          }
        }
        return true // Allow close if no changes
      }
    }

    // Clean up when component unmounts
    return () => {
      if (_onBeforeClose) {
        _onBeforeClose.current = null
      }
    }
  }, [_onBeforeClose, hasUnsavedChanges, isEditMode, confirm])

  // Track when state changes after initial load
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false
      return
    }
    // If we just created, skip marking as dirty for this render
    if (justCreatedRef.current) {
      justCreatedRef.current = false
      return
    }
    // Only mark as dirty if in edit mode
    if (isEditMode) {
      setIsDirty(true)
    }
  }, [state, isEditMode])

  // Create debounced save function
  const savePropertyDefinition = useCallback(async () => {
    if (!isEditMode || !isDirty) return

    try {
      setSaveStatus('saving')
      await onSubmit()
      setIsDirty(false)
      setSaveStatus('saved')
      logger.dev('Auto-saved property definition')

      // Reset save status after a delay
      setTimeout(() => {
        setSaveStatus('idle')
      }, 2000)
    } catch (error) {
      logger.error('Auto-save failed:', error)
      setSaveStatus('idle')
    }
  }, [isEditMode, isDirty, onSubmit])

  // Create memoized debounced function
  const debouncedSave = useMemo(
    () => debounce(savePropertyDefinition, 1500),
    [savePropertyDefinition]
  )

  // Effect to trigger auto-save
  useEffect(() => {
    if (isEditMode && isDirty && !isInitialLoad.current) {
      debouncedSave()
    }

    // Cleanup function to cancel pending saves
    return () => {
      debouncedSave.cancel()
    }
  }, [isEditMode, isDirty, debouncedSave])

  const handleAddOption = useCallback(() => {
    if (!newOptionLabel) return

    dispatch({
      type: 'ADD_OPTION',
      payload: {
        name: newOptionLabel,
        description: newOptionDefinition,
      },
    })

    setNewOptionLabel('')
    setNewOptionDefinition('')
    setShowNewOptionForm(false)
  }, [dispatch, newOptionLabel, newOptionDefinition])

  const handleRemoveOption = useCallback(
    async (id: string, label: string) => {
      try {
        await confirm({
          title: 'Remove Value',
          description: `Are you sure you want to remove "${label}"?`,
          confirmationText: 'Remove',
          cancellationText: 'Keep',
        })

        dispatch({
          type: 'REMOVE_OPTION',
          payload: id,
        })
      } catch {
        // User cancelled
      }
    },
    [confirm, dispatch]
  )

  const handleStartEditOption = useCallback(
    (option: { id: string; name: string; description?: string }) => {
      setEditingOptionKey(option.id)
      setEditedLabel(option.name)
      setEditedDefinition(option.description || '')
      setShowNewOptionForm(false)
    },
    []
  )

  const handleSaveOption = useCallback(() => {
    if (!editingOptionKey) return

    dispatch({
      type: 'EDIT_OPTION',
      payload: {
        id: editingOptionKey,
        name: editedLabel,
        description: editedDefinition,
      },
    })

    setEditingOptionKey(null)
    setEditedLabel('')
    setEditedDefinition('')
  }, [dispatch, editingOptionKey, editedLabel, editedDefinition])

  const containerSx = {
    '& .property-type-row': {
      justifyContent: 'space-between',
      ...modernButtonContainer,
    },
    '& .sidebar-tabs': {
      width: '100%',
      '& .MuiTabs-flexContainer': {
        width: '100%',
      },
      '& .MuiTab-root': {
        background: 'transparent',
        color: (theme) => theme.palette.text.primary,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        flex: 1,
        maxWidth: 'none',
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 0,
        minWidth: '0px',
        height: '30px',
        marginRight: '4px',
        transition: 'all 0.15s ease-in-out',
        letterSpacing: '-0.15px',
        fontSize: '12px',
        '&:last-child': {
          marginRight: 0,
        },
        '&.Mui-selected': {
          background: (theme) => theme.palette.top.main,
          opacity: 1,
          border: (theme) => `1px solid ${darken(theme.palette.divider, 0.05)}`,
          boxShadow: (theme) => shadows[theme.palette.mode].sm,
        },
      },
    },
  }

  logger.dev('PropertyDefinitionForm', {
    state,
    ObjectTypeMetadata,
    matches:
      ObjectTypeMetadata?.[state?.selectedDefinition?.objectTypeId]?.webEnabled,
    objectType: state?.selectedDefinition?.objectTypeId,
    meta: ObjectTypeMetadata?.[state?.selectedDefinition?.objectTypeId],
  })

  return (
    <Stack sx={{ backgroundColor: 'transparent' }}>
      {state.selectedDefinition?.id && (
        <Box sx={{ mx: -2, mt: -2 }}>
          <Tabs
            value={testMode ? 'test' : 'edit'}
            onChange={(_, value) => {
              setTestMode(value === 'test')
            }}
            className="sidebar-tabs"
            sx={{
              width: '100%',
              px: '16px',
              pt: '16px',
              pb: 0,
            }}
          >
            <Tab
              label="Edit"
              value="edit"
            />
            <Tab
              label="Test & History"
              value="test"
            />
          </Tabs>
        </Box>
      )}
      <Stack
        spacing={2}
        sx={{
          ...containerSx,
          px: '20px',
          pt: state.selectedDefinition?.id ? 3 : 0,
          pb: state.selectedDefinition?.id ? 2 : 0,
        }}
      >
        {testMode && state.selectedDefinition?.id ? (
          <PropertyTest
            propertyDefinition={state.selectedDefinition}
            workspaceId={workspaceId}
            onSwitchToEdit={() => setTestMode(false)}
          />
        ) : (
          <>
            <FormControl fullWidth>
              <InputLabel>Property Type</InputLabel>
              <Select
                value={state.propertyType || PropertyTypes.TextArea}
                label="Property Type"
                onChange={(e) =>
                  dispatch({
                    type: 'SET_PROPERTY_TYPE',
                    payload: e.target.value,
                  })
                }
              >
                {Object.entries(objectPropertyDefinitionMetadata)
                  .filter(([_, metadata]) => metadata.showUser)
                  .map(([key, metadata]) => (
                    <MenuItem
                      key={key}
                      value={key}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {React.createElement(metadata.icon, { size: 16 })}
                        {metadata.name}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              value={state.name}
              label="Property Name"
              onChange={(e) =>
                dispatch({ type: 'SET_NAME', payload: e.target.value })
              }
              fullWidth
              variant="outlined"
            />

            <TextField
              value={state.description}
              label={anyAiEnabled(state) ? 'AI Prompt' : 'Description'}
              onChange={(e) =>
                dispatch({ type: 'SET_DESCRIPTION', payload: e.target.value })
              }
              fullWidth
              variant="outlined"
              multiline={true}
              maxRows={10}
            />

            {state.propertyType && isPicklist(state.propertyType) && (
              <>
                <FormControlLabel
                  control={
                    <Tooltip
                      title={
                        state.propertyType === PropertyTypes.MultiPicklist
                          ? 'Switch to single value - Only one value from the list may be selected'
                          : 'Enable multiple values - Multiple values from the list may be selected'
                      }
                      arrow
                    >
                      <Switch
                        checked={
                          state.propertyType === PropertyTypes.MultiPicklist
                        }
                        onChange={(e) =>
                          dispatch({
                            type: 'SET_PROPERTY_TYPE',
                            payload: e.target.checked
                              ? PropertyTypes.MultiPicklist
                              : PropertyTypes.Picklist,
                          })
                        }
                      />
                    </Tooltip>
                  }
                  label={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Box className="icon-wrapper">
                        <IconCopyPlus size={16} />
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Allow multiple values to be selected
                      </Typography>
                    </Stack>
                  }
                />
              </>
            )}

            <FormControlLabel
              control={
                <Tooltip
                  title={
                    state.aiManaged
                      ? 'Disable auto-population - Day.ai will stop adding values to this field'
                      : 'Enable auto-population - Day.ai will start adding values it detects through reasoning'
                  }
                  arrow
                >
                  <Switch
                    checked={state.aiManaged}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_AI_ENABLED',
                        payload: e.target.checked,
                      })
                    }
                  />
                </Tooltip>
              }
              label={
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Box className="icon-wrapper">
                    <IconSparkles size={16} />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {state.aiManaged
                      ? 'Populate with AI. Manual edits will take precedence over AI-generated values.'
                      : 'Do not populate with AI. Day.ai will not use its reasoning to fill in values for this field.'}
                  </Typography>
                </Stack>
              }
            />

            <FormControlLabel
              disabled={
                !ObjectTypeMetadata?.[state?.selectedDefinition?.objectTypeId]
                  ?.webEnabled
              }
              control={
                <Tooltip
                  title={
                    ObjectTypeMetadata?.[
                      state?.selectedDefinition?.objectTypeId
                    ]?.webEnabled
                      ? state.useWeb
                        ? 'Switch to disable web scraping - Day.ai will not use web data for this field'
                        : 'Enable web scraping - Day.ai will use web data to populate this field'
                      : `Web research is not available for ${
                          ObjectTypeMetadata?.[
                            state?.selectedDefinition?.objectTypeId
                          ]?.label
                        }`
                  }
                  arrow={true}
                >
                  <Switch
                    disabled={
                      !ObjectTypeMetadata?.[
                        state?.selectedDefinition?.objectTypeId
                      ]?.webEnabled
                    }
                    checked={state.useWeb}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_USE_WEB',
                        payload: e.target.checked,
                      })
                    }
                  />
                </Tooltip>
              }
              label={
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Box className="icon-wrapper">
                    <IconBrowser size={16} />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      opacity: !ObjectTypeMetadata?.[
                        state?.selectedDefinition?.objectTypeId
                      ]?.webEnabled
                        ? 0.5
                        : 1,
                    }}
                  >
                    {ObjectTypeMetadata?.[
                      state?.selectedDefinition?.objectTypeId
                    ]?.webEnabled
                      ? state.useWeb
                        ? 'Use data from the web. Manual edits take precedence.'
                        : 'Day.ai will not use web data to populate this field.'
                      : `Web research is available for People & Orgs only, not for ${
                          ObjectTypeMetadata?.[
                            state?.selectedDefinition?.objectTypeId
                          ]?.label
                        }`}
                  </Typography>
                </Stack>
              }
            />

            {state.propertyType && isPicklist(state.propertyType) && (
              <>
                <Row
                  gap={2}
                  className="possible-values"
                >
                  <Typography
                    variant="h4"
                    className="possible-values-header"
                  >
                    Possible Values {state.name ? `for ${state.name}` : ''}
                  </Typography>
                </Row>

                <Row
                  gap={1}
                  className="options-row"
                >
                  {state.options.map((option) => (
                    <MetadataChip
                      key={option.id}
                      state={{
                        label: option.name,
                        value: option.id,
                        color: 'secondary',
                      }}
                      icon={<IconCircleFilled />}
                      onClick={() => handleStartEditOption(option)}
                    />
                  ))}
                  {!editingOptionKey && (
                    <MetadataChip
                      state={{
                        label: 'Add Option',
                        value: 'new',
                        color: 'default',
                      }}
                      icon={<IconPlus size={16} />}
                      onClick={() => setShowNewOptionForm(true)}
                    />
                  )}
                </Row>

                <Stack spacing={2}>
                  {editingOptionKey && (
                    <Box className="editing-stack">
                      <Stack spacing={1}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                        >
                          Editing &ldquo;
                          {
                            state.options.find((o) => o.id === editingOptionKey)
                              ?.name
                          }
                          &rdquo;
                        </Typography>
                        <Stack spacing={1}>
                          <TextField
                            label="Option label"
                            value={editedLabel}
                            onChange={(e) => setEditedLabel(e.target.value)}
                            fullWidth
                            size="small"
                            autoFocus
                          />
                          <TextField
                            label="Definition (used by AI to populate)"
                            value={editedDefinition}
                            onChange={(e) =>
                              setEditedDefinition(e.target.value)
                            }
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                          />
                          <Row gap={1}>
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              onClick={handleSaveOption}
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setEditingOptionKey(null)}
                            >
                              Cancel
                            </Button>
                            <Box sx={{ flex: 1 }} />
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => {
                                const option = state.options.find(
                                  (o) => o.id === editingOptionKey
                                )
                                if (option) {
                                  handleRemoveOption(option.id, option.name)
                                }
                              }}
                            >
                              Remove
                            </Button>
                          </Row>
                        </Stack>
                      </Stack>
                    </Box>
                  )}

                  {!editingOptionKey && showNewOptionForm && (
                    <Stack spacing={1}>
                      <TextField
                        label="New option label"
                        value={newOptionLabel}
                        onChange={(e) => setNewOptionLabel(e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Definition (used by AI to populate)"
                        value={newOptionDefinition}
                        onChange={(e) => setNewOptionDefinition(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                      />
                      <Row gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddOption}
                          disabled={!newOptionLabel}
                          startIcon={<IconPlus size={16} />}
                        >
                          Add Option
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setShowNewOptionForm(false)}
                        >
                          Cancel
                        </Button>
                      </Row>
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </Stack>
  )
}

export default PropertyDefinitionForm
